import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field } from 'react-final-form';
import { Button, Grid, Table } from 'semantic-ui-react';
import { ProcedureTaxPoint } from 'ts-types/api.types';
import DatePicker from 'components/final-form/DatePicker';
import { composeValidators, mustBeNumber, required } from 'utils/validatorUtils';
import Input from 'components/final-form/Input';
import styled from 'styled-components';
import moment from 'moment';
import { FieldArray } from 'react-final-form-arrays';
import { numberFormatter } from 'utils/fomattingUtils';
import EditActionButton from 'components/EditActionButton';
import SaveActionButton from 'components/SaveActionButton';
import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';

const ScrollableTableContainer = styled.div`
  height: 252px;
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34,36,38,.15);
  box-shadow: none;
  border-radius: .28571429rem;

  & .ui.table {
    border: none;
  }

   & .ui.table thead tr:first-child > th {
      position: sticky !important;
      top: 0;
      z-index: 2;
      background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
      border-bottom: 1px solid rgba(34,36,38,.15)
  }
`;

const DateCell = styled(Table.Cell)`
   & .field {
    width: 65px;
   }
`;

const addButton = {
  marginBottom: 0,
  marginTop: 10
};

const priceInput = {
  width: "60px"
};

const priceDate = {
  width: "90px",
  fontSize: "12px"
};

interface Props extends MessageSourceProps {
  procedureTaxPoint?: Array<ProcedureTaxPoint>,
  tarmedTariff: Boolean,
  tariffCode?: String
}

interface State {
  procedureTaxPoint: Array<Partial<ProcedureTaxPoint>>,
  editTaxPoint: boolean,
  editTaxPointIndex?: number,
  taxPointCopy?: Partial<ProcedureTaxPoint>,
  errors: Array<boolean>
}

class ProcedureTaxPointsSegment extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {procedureTaxPoint = []} = this.props;

    this.state = {
      procedureTaxPoint: procedureTaxPoint,
      editTaxPoint: false,
      editTaxPointIndex: undefined,
      errors: [false, false, false]
    }
  }

  validateFields = (taxPoint: Partial<ProcedureTaxPoint>): boolean => {
    let errors: Array<boolean> = [];

    errors[0] = taxPoint.fromDate == "";
    errors[1] = !taxPoint.fromAmount;

    if (taxPoint.fromAmount) {
      if (+taxPoint.fromAmount !== +taxPoint.fromAmount) {
        errors[1] = true;
      }
    }

    if (taxPoint.toAmount) {
      if (!(+taxPoint.toAmount === +taxPoint.toAmount)) {
        errors[2] = true;
      }
    }

    this.setState({
      errors: errors
    });

    return errors.includes(true);
  };

  addTaxPoint = (e: any, fields: any, index: number) => {

    e.preventDefault();

    if (fields.valid) {
      fields.push({
        fromDate: moment().format("YYYY-MM-DD"),
        fromAmount: "",
        toAmount: ""
      });

      this.setState({
        editTaxPoint: true,
        editTaxPointIndex: index,
        taxPointCopy: undefined
      })
    }

  };

  saveTaxPoint = (fields: any, index: number) => {

    this.validateFields(fields.value[index]);

    fields.value.sort(function (d1: any, d2: any) {
      //@ts-ignore
      return new Date(d2.fromDate) - new Date(d1.fromDate);
    });

    if (fields.valid) {
      this.setState({
        editTaxPoint: false,
        editTaxPointIndex: undefined
      });
    }

  };

  editTaxPoint = (index: number, field: Partial<ProcedureTaxPoint>, fields: any): void => {

    if (this.state.editTaxPoint) {
      fields.remove(this.state.editTaxPointIndex);
      fields.insert(this.state.editTaxPointIndex, this.state.taxPointCopy);

      this.setState({
        editTaxPointIndex: index,
        taxPointCopy: field
      });
    } else {
      this.setState({
        editTaxPoint: true,
        editTaxPointIndex: index,
        taxPointCopy: field
      })
    }

  };

  cancelEditTaxPoint = (fields: any, index: number) => {

    this.setState({
      editTaxPoint: false,
      editTaxPointIndex: undefined
    });

    fields.remove(index);
    if (this.state.taxPointCopy) {
      fields.insert(index, this.state.taxPointCopy);
    }

  };

  render(): React.ReactNode {

    const {getMessage, tarmedTariff, tariffCode} = this.props;
    const {editTaxPointIndex, editTaxPoint, errors} = this.state;

    const validator = (value: string) => required(value);

    const tarmedProcedureHeaderFields = (
        <React.Fragment>
          <Table.HeaderCell width={1}>{getMessage('procedure.tax.medicalValue')}</Table.HeaderCell>
          <Table.HeaderCell width={1}>{getMessage('procedure.tax.assistantValue')}</Table.HeaderCell>
          <Table.HeaderCell width={1}>{getMessage('procedure.tax.technicalValue')}</Table.HeaderCell>
        </React.Fragment>
    );

    const tarmedProcedureFieldValues = (taxPoint: ProcedureTaxPoint) => {
      return (
          <React.Fragment>
            <Table.Cell width={1}>
              {taxPoint.medicalValue != null ? numberFormatter.format(taxPoint.medicalValue) : null}
            </Table.Cell>
            <Table.Cell width={1}>
              {taxPoint.assistantValue != null ? numberFormatter.format(taxPoint.assistantValue) : null}
            </Table.Cell>
            <Table.Cell width={1}>
              {taxPoint.technicalValue != null ? numberFormatter.format(taxPoint.technicalValue) : null}
            </Table.Cell>
          </React.Fragment>
      );
    };

    return (
        <React.Fragment>
          <Grid.Column width={11}>
            {
              <ScrollableTableContainer>
                <Table celled selectable size='small'>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell width={2}>{getMessage('from')}</Table.HeaderCell>
                      <Table.HeaderCell width={2}>{getMessage('procedure.from.amount')}</Table.HeaderCell>
                      {tarmedTariff && !editTaxPoint ? tarmedProcedureHeaderFields : null}
                      {
                        (tarmedTariff && editTaxPoint) || !tarmedTariff ?
                            <Table.HeaderCell width={2}>{getMessage('procedure.to.amount')}</Table.HeaderCell> :
                            null
                      }
                      {tariffCode != "001" ?
                          <Table.HeaderCell width={2}>{this.props.getMessage('actions')}</Table.HeaderCell> : null
                      }
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <FieldArray name="procedureTaxPoints">
                      {({fields}) =>
                          fields.map((field, index) => {
                                const taxPoint = fields.value[index];
                                if (editTaxPoint && editTaxPointIndex === index) {
                                  return (
                                      <Table.Row key={index}>
                                        <DateCell width={2}>
                                          <Field
                                              name={`${field}.fromDate`}
                                              component={DatePicker}
                                              style={priceDate}
                                              validate={validator}
                                              size='mini'
                                              meta={{touched: errors[0], error: errors[0]}}
                                          />
                                        </DateCell>
                                        <Table.Cell width={2}>
                                          <Field
                                              name={`${field}.fromAmount`}
                                              component={Input}
                                              style={priceInput}
                                              validate={composeValidators(required, mustBeNumber)}
                                              size='mini'
                                              error={errors[1]}
                                          />
                                        </Table.Cell>
                                        {tarmedTariff && !editTaxPoint ? tarmedProcedureFieldValues(taxPoint) : null}
                                        <Table.Cell width={2}>
                                          <Field
                                              name={`${field}.toAmount`}
                                              component={Input}
                                              style={priceInput}
                                              validate={mustBeNumber}
                                              size='mini'
                                              error={errors[2]}
                                          />
                                        </Table.Cell>
                                        <Table.Cell width={2}>
                                          <SaveActionButton hoverMessage={getMessage('button.save')}
                                                            onConfirm={() => this.saveTaxPoint(fields, index)}/>
                                          <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                                              onConfirm={() => this.cancelEditTaxPoint(fields, index)}/>
                                        </Table.Cell>
                                      </Table.Row>
                                  )

                                } else {
                                  const taxPoint = fields.value[index];

                                  if (taxPoint !== undefined) {
                                    return (
                                        <Table.Row
                                            key={index}
                                        >
                                          <Table.Cell width={2}>
                                            {moment(taxPoint.fromDate).format("DD.MM.YYYY")}
                                          </Table.Cell>
                                          <Table.Cell width={2}>
                                            {numberFormatter.format(taxPoint.fromAmount)}
                                          </Table.Cell>
                                          {tarmedTariff && !editTaxPoint ? tarmedProcedureFieldValues(taxPoint) : null}
                                          {
                                            (tarmedTariff && editTaxPoint) || !tarmedTariff ? <Table.Cell width={2}>
                                              {numberFormatter.format(taxPoint.toAmount)}
                                            </Table.Cell> : null
                                          }
                                          {tariffCode != "001" ?
                                              <Table.Cell width={2}>
                                                <EditActionButton
                                                    hoverMessage={getMessage('edit')}
                                                    onConfirm={() => this.editTaxPoint(index, taxPoint, fields)}
                                                />
                                                <DeleteActionButton
                                                    hoverMessage={getMessage('msgDelete')}
                                                    onConfirm={() => {
                                                      fields.remove(index)
                                                    }}
                                                />
                                              </Table.Cell> : null
                                          }
                                        </Table.Row>
                                    )
                                  }
                                }
                              }
                          )
                      }
                    </FieldArray>
                  </Table.Body>
                </Table>
              </ScrollableTableContainer>
            }
            {tariffCode != "001" ?
                <FieldArray name="procedureTaxPoints">
                  {({fields}) => (
                      <Button style={addButton} compact primary onClick={(e) => this.addTaxPoint(e, fields, fields.length!)}>
                        {this.props.getMessage('button.add')}
                      </Button>
                  )
                  }
                </FieldArray> : null
            }
          </Grid.Column>
        </React.Fragment>
    );
  }

}

export default withMessages(ProcedureTaxPointsSegment);
